import { useCallback } from "react";
import { cn } from "utils/helpers";
import { formatCurrency, getDescriptionCellValue } from "./utils";
import type { QuoteTableColumn, QuoteTableMode } from "./quote-table.types";
import type { RfxQuoteItem } from "../../Submission/types/quote.type";
import type { PastedRowData, UnitOption } from "./DynamicQuoteTable";
import { CompactTextInput } from "newui/components/ui/InputWithLabel";
import { CompactTextAreaInput } from "newui/components/ui/InputWithLabel/CompactTextAreaInput";

export interface QuoteTableCellProps {
	allValues?: Record<string, Record<string, number | string>>;
	column: QuoteTableColumn;
	columns: QuoteTableColumn[];
	columnValues?: Record<number, Record<number, string>>;
	dirtyRowValues?: Record<number, { unit_id?: number | null; name?: string; }>;
	disabled: boolean;
	editValues?: Record<number, Record<number, { quantity: string; rate: string }>>;
	item: RfxQuoteItem;
	mode: QuoteTableMode;
	rowErrors?: Record<number, { unit_id?: boolean; cells?: Record<number, boolean>; name?: boolean; }>;
	unitOptions?: UnitOption[];
	onCellValueChange?: (itemId: number, columnId: number, field: string, value: string) => void;
	onFormikFieldChange?: (itemId: number, field: string, value: number | string) => void;
	onPasteRows?: (currentItemId: number, currentRowCells: Record<number, string>, newRows: PastedRowData[]) => void;
	onRowUpdate?: (rowId: number, payload: { unit_id?: number | null; name?: string; }) => void;
}

const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
	if (event.key === "-") {
		event.preventDefault();
	}
};

const QuoteTableCell: React.FC<QuoteTableCellProps> = ({
	allValues = {},
	column: col,
	columns,
	columnValues = {},
	disabled,
	dirtyRowValues = {},
	editValues = {},
	item,
	mode,
	onCellValueChange,
	onFormikFieldChange,
	onPasteRows,
	onRowUpdate,
	rowErrors = {},
	unitOptions = [],
}) => {
	const isProcurerView = mode === "edit";
	const isClientView = mode === "view";

	// --- Paste matrix handler for description cells ---
	const sortedCols = [...columns].sort((a, b) => a.sortOrder - b.sortOrder);

	const handleDescriptionPaste = useCallback(
		(event: React.ClipboardEvent<HTMLTextAreaElement>, colId: number) => {
			if (!onPasteRows) {
				return;
			}
			const text = event.clipboardData.getData("text");
			// Only intercept multi-cell pastes (tab or newline separated)
			if (!text.includes("\t") && !text.includes("\n")) {
				return;
			}
			event.preventDefault();

			const matrix = text
				.replace(/\r\n/g, "\n")
				.replace(/\r/g, "\n")
				.split("\n")
				.filter((line, idx, arr) => !(idx === arr.length - 1 && line === ""))
				.map((line) => line.split("\t"));

			if (matrix.length === 0) {
				return;
			}

			const startColIndex = sortedCols.findIndex((c) => c.id === colId);
			if (startColIndex === -1) {
				return;
			}

			// Build cells for each pasted row, mapping columns left-to-right
			const buildRowCells = (pastedRow: string[]): Record<number, string> => {
				const cells: Record<number, string> = {};
				for (let dx = 0; dx < pastedRow.length; dx++) {
					const destCol = sortedCols[startColIndex + dx];
					if (!destCol) {
						break;
					}
					if (destCol.columnType === "description") {
						cells[destCol.id] = pastedRow[dx];
					}
				}
				return cells;
			};

			// First row updates the current item
			const currentRowCells = buildRowCells(matrix[0]);

			// Remaining rows are new rows to create
			const newRows: PastedRowData[] = matrix.slice(1).map((pastedRow) => ({
				cells: buildRowCells(pastedRow),
			}));

			onPasteRows(item.id, currentRowCells, newRows);
		},
		[item.id, onPasteRows, sortedCols]
	);

	if (col.columnType === "description") {
		// Description cells follow the supplier-wins merge: a supplier-owned cell
		// (rfxInviteId set, used by supplier-created rows) takes precedence over
		// the procurer-fixed cell (rfxInviteId === null, used by client rows).
		// Use getDescriptionCellValue so supplier-created rows render their own
		// description rather than collapsing to "-" via the procurer-only path.
		// columnValues holds in-progress procurer edits (edit mode) and overrides.
		const value =
			columnValues[item.id]?.[col.id] ?? getDescriptionCellValue(item, col.id);

		// Description cells are procurer-fixed: only the procurer (edit mode) writes
		// them. Every other mode — supplier (respond), client (view), or disabled —
		// renders the value read-only.
		if (!isProcurerView || disabled || !onCellValueChange) {
			return <span className="text-gray-900 whitespace-pre-wrap break-words">{value || "-"}</span>;
		}

		const isPrimaryDescCol = sortedCols.find((c) => c.columnType === "description")?.id === col.id;
		const hasCellError = isPrimaryDescCol
			? rowErrors[item.id]?.name === true
			: rowErrors[item.id]?.cells?.[col.id] === true;
		return (
			<CompactTextAreaInput
				hasError={hasCellError}
				value={value}
				onChange={(e) => {
					onCellValueChange?.(item.id, col.id, "value", e.target.value);
				}}
				onPaste={(e) => handleDescriptionPaste(e, col.id)}
				placeholder="Enter description"
				disabled={disabled}
				data-testid={`desc-col-${col.id}-item-${item.id}`}
			/>
		);
	}

	if (col.columnType === "response_text") {
		const formikField = `text_${col.id}`;
		const key = `quote-${item.id}`;
		const formikValue = allValues[key]?.[formikField];
		const value = formikValue !== undefined && formikValue !== null
			? String(formikValue)
			: (columnValues[item.id]?.[col.id] ?? "");
		if (isProcurerView || (isClientView && !value)) {
			return <span className="text-sm italic text-gray-400">Supplier response</span>;
		}
		if (isClientView) {
			return <span className="text-gray-900 whitespace-pre-wrap break-words">{value || "-"}</span>;
		}
		return (
			<CompactTextAreaInput
				value={value}
				onChange={(e) => {
					onFormikFieldChange?.(item.id, formikField, e.target.value);
				}}
				placeholder="Enter response"
				disabled={disabled}
				data-testid={`resp-col-${col.id}-item-${item.id}`}
			/>
		);
	}

	if (col.columnType === "response_number" || col.columnType === "response_quantity" || col.columnType === "total") {
		const key = `quote-${item.id}`;
		const formikValues = allValues[key];
		const hasCellError = rowErrors[item.id]?.cells?.[col.id] === true;
		const isPercentValue = dirtyRowValues[item.id]?.unit_id ?? item.unitId;

		// Auto-calculate total. "Numeric response" columns are response_value AND
		// saving (saving cells are stored as negative numbers, so they naturally
		// subtract). The quantity multiplier applies ONLY when the table has
		// exactly one numeric response column; otherwise every numeric cell is a
		// flat amount — see RfxQuoteResponseTotalService for the backend mirror.
		if (col.columnType === "total" && col.isSupplierEditable === false) {
			const numericResponseColIds = columns
				.filter((c) => c.columnType === "response_value" || c.columnType === "saving")
				.map((c) => c.id);
			const quantityCol = columns.find((c) => c.columnType === "response_quantity");
			const rawQty = quantityCol
				? Number(formikValues?.["quantity"] ?? columnValues[item.id]?.[quantityCol.id] ?? 0)
				: 0;
			const useMultiplier =
				numericResponseColIds.length === 1 && quantityCol && rawQty > 0;
			const total = numericResponseColIds.reduce((sum, colId) => {
				const formikVal = formikValues?.[`rate_${colId}`];
				const val = Number(formikVal ?? columnValues[item.id]?.[colId] ?? editValues[item.id]?.[colId]?.rate ?? 0);
				return sum + (useMultiplier ? rawQty * val : val);
			}, 0);
			return (
				<span className="text-right pl-2 pr-4 block text-gray-900">
					{formatCurrency(isNaN(total) ? 0 : total)}
				</span>
			);
		}

		// Default columns: read/write Formik values (quantity for number, rate for currency)
		const formikField = col.columnType === "response_quantity" ? "quantity" : "rate";
		const formikValue = formikValues?.[formikField];
		const hasFormikValue = formikValue !== undefined && formikValue !== null;
		const value = hasFormikValue ? String(formikValue) : (columnValues[item.id]?.[col.id] ?? "");

		if (isProcurerView) {
			if (col.columnType === "total") {
				return <span className="px-2 text-right block text-sm text-gray-400">&mdash;</span>;
			}
			return <span className="text-sm text-gray-400">0</span>;
		}

		if (isClientView || disabled) {
			const display = col.columnType === "total" && value
				? formatCurrency(Number(value))
				: value || "-";
			return <span className={cn("text-gray-900", col.columnType === "total" && "text-right block")}>{display}</span>;
		}

		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const numericValue = parseFloat(e.target.value);
			const safeValue = isNaN(numericValue) || numericValue < 0 ? "" : numericValue;
			if (hasFormikValue || onFormikFieldChange) {
				onFormikFieldChange?.(item.id, formikField, safeValue);
			} else {
				onCellValueChange?.(item.id, col.id, "value", e.target.value);
			}
		};

		return (
			<div className={col.columnType === "total" ? "relative flex" : undefined}>
				{col.columnType === "total" && (
					<span className="absolute left-2 top-1/2 z-10 -translate-y-1/2 text-sm">$</span>
				)}
				<CompactTextInput
					type="number"
					value={value === "" ? "" : value}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					placeholder="0"
					min={0}
					step={col.columnType === "total" ? 1 : undefined}
					disabled={disabled}
					data-testid={`num-col-${col.id}-item-${item.id}`}
					required
					hasError={hasCellError}
				/>
			</div>
		);
	}

	if (col.columnType === "unit_of_measurement") {
		if (isProcurerView && !disabled && onRowUpdate && unitOptions.length > 0) {
			return (
				<select
					className={cn(
						"w-full rounded border bg-white px-1 py-1 text-sm",
						rowErrors[item.id]?.unit_id ? "border-red-500" : "border-gray-300"
					)}
					value={dirtyRowValues[item.id]?.unit_id ?? item.unitId ?? ""}
					onChange={(e) => {
						const val = e.target.value;
						onRowUpdate(item.id, { unit_id: val ? Number(val) : null });
					}}
					data-testid={`row-unit-select-${item.id}`}
				>
					<option value="">-</option>
					{unitOptions.map((opt) => (
						<option key={opt.value} value={opt.value}>
							{opt.label}
						</option>
					))}
				</select>
			);
		}
		return <span className="text-gray-900">{item.unitMeasurement ?? "-"}</span>;
	}

	if (col.columnType === "response_value") {
		const formikField = `rate_${col.id}`;
		const key = `quote-${item.id}`;
		const formikValue = allValues[key]?.[formikField];
		const value = formikValue !== undefined && formikValue !== null
			? String(formikValue)
			: (columnValues[item.id]?.[col.id] ?? editValues[item.id]?.[col.id]?.rate ?? "");
		const hasCellError = rowErrors[item.id]?.cells?.[col.id] === true;

		if (isProcurerView) {
			return <span className="text-sm text-gray-400">{formatCurrency(0)}</span>;
		}
		if (disabled || isClientView) {
			return <span className="text-gray-900">{value ? formatCurrency(Number(value)) : "-"}</span>;
		}

		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const numericValue = parseFloat(e.target.value);
			const safeValue = isNaN(numericValue) || numericValue < 0 ? "" : numericValue;
			onFormikFieldChange?.(item.id, formikField, safeValue);
		};

		return (
			<div className="relative flex">
				<span className="absolute left-2 top-1/2 z-10 -translate-y-1/2 text-sm text-gray-500">$</span>
				<CompactTextInput
					type="number"
					hasError={hasCellError}
					value={value === "" ? "" : value}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					min={0}
					disabled={disabled}
					placeholder="0"
					data-testid={`val-col-${col.id}-item-${item.id}`}
					required
				/>
			</div>
		);
	}

	if (col.columnType === "saving") {
		// Saving cells share the `rate_${col.id}` formik field shape with
		// response_value (so totals math is uniform), but values are STORED as
		// negative numbers. The supplier types a positive amount (e.g. 500) and
		// we negate on commit; readonly views format the stored negative value
		// so it renders as "-$500.00".
		const formikField = `rate_${col.id}`;
		const key = `quote-${item.id}`;
		const formikValue = allValues[key]?.[formikField];
		const storedRaw = formikValue !== undefined && formikValue !== null
			? String(formikValue)
			: (columnValues[item.id]?.[col.id] ?? editValues[item.id]?.[col.id]?.rate ?? "");
		const storedNum = storedRaw === "" ? null : Number(storedRaw);
		const hasCellError = rowErrors[item.id]?.cells?.[col.id] === true;

		if (isProcurerView) {
			return <span className="text-sm italic text-red-300">{formatCurrency(0)}</span>;
		}
		if (disabled || isClientView) {
			// Saving is a discount — render in red so it's visually obvious the
			// value reduces the table total.
			return (
				<span className="font-semibold text-red-600">
					{storedNum === null || isNaN(storedNum)
						? "-"
						: formatCurrency(storedNum)}
				</span>
			);
		}

		// The visible input shows the absolute value the supplier typed; the
		// "-$" prefix and the negated storage are handled internally so the
		// supplier never has to think about the sign.
		const displayValue =
			storedNum === null || isNaN(storedNum)
				? ""
				: String(Math.abs(storedNum));

		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const numericValue = parseFloat(e.target.value);
			if (isNaN(numericValue) || numericValue < 0) {
				onFormikFieldChange?.(item.id, formikField, "");
				return;
			}
			const negated = numericValue === 0 ? 0 : -Math.abs(numericValue);
			onFormikFieldChange?.(item.id, formikField, negated);
		};

		return (
			<div className="relative flex">
				<span className="absolute left-2 top-1/2 z-10 -translate-y-1/2 text-sm text-red-600">
					-$
				</span>
				<CompactTextInput
					type="number"
					hasError={hasCellError}
					inputClassName="pl-7"
					value={displayValue}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					min={0}
					disabled={disabled}
					placeholder="0"
					data-testid={`saving-col-${col.id}-item-${item.id}`}
					required
				/>
			</div>
		);
	}

	return null;
};

export { QuoteTableCell };
